import { Trans, useLingui } from '@lingui/react/macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useActiveBrandId } from '../lib/active-brand.js';
import {
  archiveNotification,
  type Brand,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationItem,
} from '../lib/api.js';
import { formatDate } from '../lib/format.js';
import {
  brandsQuery,
  notificationsListQuery,
  notificationUnreadCountQuery,
} from '../lib/queries.js';
import { BrandChip } from './brand-chip.js';
import { BrandSparkle } from './mag.js';
import { useNotificationDrawer } from './notification-drawer.js';

// Top-nav notifications chip (s178.7g — visual rework).
//
// Matches the chrome around it: same height as the brand switcher,
// monospace caps for the count so it reads as part of the same
// family rather than a generic UI element.
//
// Three visual tiers, lowest-volume idle, highest on hover:
//   - empty       → circular white chip, ink/15 border, ink/55
//                   glyph. quiet; doesn't compete with the brand
//                   pills next to it.
//   - unread idle → white pill, ink/15 border, spell dot + count.
//                   reads as "something's waiting" without yelling.
//   - unread hover→ spell-yellow background, ink border, plum 3px
//                   shadow, 2px lift. matches the brand's btn-spell
//                   hover language so the chip becomes a call-to-
//                   action the moment the founder reaches for it.
//
// Click opens an inline dropdown instead of navigating to
// /notifications. The dropdown shows the most recent items with
// per-row mark-read / archive actions + their CTA link; founders can
// triage from anywhere in the app without losing their place. The
// /notifications page still exists for the full archive view; the
// dropdown footer links there.
//
// Cross-brand scope by default — the inbox + brand switcher handle
// per-brand filtering. Founders see every brand's pending signals
// from any page so a quiet brand can't hide an alert.

export function NotificationsBell() {
  const { data: count = 0 } = useQuery(notificationUnreadCountQuery('all'));
  const hasUnread = count > 0;
  const label = count > 99 ? '99+' : String(count);

  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Click-outside + Escape to close. Listening on mousedown (not
  // click) so the close fires before any button inside the dropdown
  // runs its own handler — same pattern as the brand-switcher.
  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // §12.2L Tier 1 — pulse the chip briefly when the unread count
  // ticks up (0 → ≥1, or N → N+M). prevCountRef tracks the last
  // count value so we only pulse on increases, not on decreases
  // (user reading + clearing notifications). pulse class is
  // mag-pulse — same keyframe the running-step glyph uses, so the
  // visual vocabulary stays consistent.
  const prevCountRef = useRef(count);
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    if (count > prevCountRef.current) {
      setPulse(true);
      const t = window.setTimeout(() => setPulse(false), 1500);
      return () => window.clearTimeout(t);
    }
    prevCountRef.current = count;
    return undefined;
  }, [count]);
  useEffect(() => {
    // Once the pulse animation finishes, update the ref so the next
    // tick-up fires another pulse.
    if (!pulse) prevCountRef.current = count;
  }, [pulse, count]);

  return (
    <div className="relative" ref={wrapRef}>
      <TriggerButton
        hasUnread={hasUnread}
        label={label}
        pulse={pulse}
        open={open}
        onToggle={() => setOpen((v) => !v)}
      />
      {open ? <NotificationsDropdown onClose={() => setOpen(false)} /> : null}
    </div>
  );
}

interface TriggerProps {
  hasUnread: boolean;
  label: string;
  pulse: boolean;
  open: boolean;
  onToggle: () => void;
}

function TriggerButton({ hasUnread, label, pulse, open, onToggle }: TriggerProps) {
  const { t } = useLingui();
  if (hasUnread) {
    // Border color is part of an exclusive pair (open vs. closed) so
    // the two states don't overlap in the class string — Tailwind v4
    // emits both `border-[var(--ink)]/15` and `border-[var(--ink)]`
    // as regular utilities, and cascade order isn't deterministic
    // for caller-supplied class strings. Splitting the conditional
    // means exactly one border-color is applied at a time, and the
    // open state actually shows the ink border instead of inheriting
    // the muted /15 from the base.
    return (
      <button
        type="button"
        aria-label={`${label} unread notifications`}
        aria-expanded={open}
        title={`${label} unread`}
        onClick={onToggle}
        className={`group relative inline-flex h-7 items-center gap-1.5 overflow-hidden rounded-full border-[1.5px] bg-white pl-3 pr-2.5 font-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.08em] no-underline transition-[transform,box-shadow,border-color,color] duration-150 hover:-translate-y-0.5 hover:border-[var(--ink)] hover:text-[var(--ink)] hover:shadow-[3px_3px_0_var(--plum)] ${
          open
            ? '-translate-y-0.5 border-[var(--ink)] text-[var(--ink)] shadow-[3px_3px_0_var(--plum)]'
            : 'border-[var(--ink)]/15 text-[var(--ink)]/75'
        } ${pulse ? 'mag-pulse' : ''}`}
      >
        {/* Spell-yellow "paint": a small dot at rest that grows on
            hover (or while open) to fill the chip's background,
            sweeping from the left toward the bell + count. */}
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute left-1.5 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-[var(--spell)] transition-all duration-[260ms] ease-out group-hover:left-0 group-hover:top-0 group-hover:h-full group-hover:w-full group-hover:translate-y-0 ${
            open ? '!left-0 !top-0 !h-full !w-full !translate-y-0' : ''
          }`}
        />
        <span className="relative inline-flex items-center">
          <BellGlyph ringing={!open} />
        </span>
        <span className="relative tabular-nums">{label}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      aria-label={t`notifications`}
      aria-expanded={open}
      title={t`notifications`}
      onClick={onToggle}
      className={`tap-44 inline-flex h-7 w-7 items-center justify-center rounded-full border bg-white no-underline transition-colors duration-150 hover:border-[var(--ink)] hover:text-[var(--ink)] ${
        open ? 'border-[var(--ink)] text-[var(--ink)]' : 'border-[var(--line)] text-[var(--ink)]/55'
      }`}
    >
      <BellGlyph />
    </button>
  );
}

function NotificationsDropdown({ onClose }: { onClose: () => void }) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { openNotification } = useNotificationDrawer();
  // Match the /notifications page's query (no params → server defaults
  // to brand-scope 'all', filter 'all', non-archived). Same key as the
  // inbox = one cache entry for both surfaces, instant fill when the
  // inbox has already loaded once.
  const { data, isLoading } = useQuery({
    ...notificationsListQuery({}),
    staleTime: 0,
  });
  // brandsQuery is already warmed by TopNav's BrandSwitcher on every
  // page that renders this bell — keying the same cache here gives
  // instant brand-chip hydration with no extra network call.
  const { data: brands = [] } = useQuery(brandsQuery());
  const brandsById = useMemo(() => {
    const m = new Map<string, Brand>();
    for (const b of brands) m.set(b.id, b);
    return m;
  }, [brands]);
  // Cap at 10 for the dropdown; the full archive view is at /notifications.
  const items = (data?.notifications ?? []).slice(0, 10);
  const unreadCount = items.filter((n) => n.readAt === null).length;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const markRead = useMutation({
    mutationFn: (id: string) => markNotificationRead(id, true),
    onSuccess: invalidate,
  });
  const archive = useMutation({
    mutationFn: (id: string) => archiveNotification(id, true),
    onSuccess: invalidate,
  });
  const markAllRead = useMutation({
    mutationFn: () => markAllNotificationsRead('all'),
    onSuccess: invalidate,
  });

  return (
    <div
      role="dialog"
      aria-label={t`notifications`}
      className="mag-dropdown-in absolute right-0 top-full z-40 mt-2 w-[min(380px,calc(100vw-2rem))] overflow-hidden rounded-lg border border-[var(--ink)] bg-white shadow-[4px_4px_0_var(--plum)]"
    >
      <header className="flex items-center justify-between gap-3 border-b border-[var(--line)] px-3 py-2">
        <p className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/55">
          {'↘'} <Trans>notifications</Trans>
          {unreadCount > 0 ? <Trans> · {unreadCount} unread</Trans> : null}
        </p>
        {unreadCount > 0 ? (
          <button
            type="button"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
            className="rounded px-1 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--plum)] transition-colors duration-150 hover:bg-[var(--spell-soft)]/40 hover:text-[var(--ink)] disabled:opacity-50"
          >
            <Trans>mark all read</Trans>
          </button>
        ) : null}
      </header>

      <div className="max-h-[420px] overflow-y-auto">
        {isLoading ? (
          <DropdownLoading />
        ) : items.length === 0 ? (
          <DropdownStatus>
            <Trans>nothing here yet. i'll ping you when there's news.</Trans>
          </DropdownStatus>
        ) : (
          <ul className="flex flex-col">
            {items.map((n, i) => (
              <NotificationRow
                key={n.id}
                rowIndex={i}
                notification={n}
                brand={n.brandId ? (brandsById.get(n.brandId) ?? null) : null}
                onMarkRead={() => markRead.mutate(n.id)}
                onArchive={() => archive.mutate(n.id)}
                onOpen={() => {
                  // Open the global details drawer, then close the
                  // dropdown so the drawer isn't peeking out from
                  // behind it. The drawer marks the row read on open.
                  openNotification(n);
                  onClose();
                }}
              />
            ))}
          </ul>
        )}
      </div>

      <footer className="flex items-center justify-between gap-2 border-t border-[var(--line)] bg-[var(--paper)] px-3 py-2">
        <PreferencesLink onClose={onClose} />
        <Link
          to="/notifications"
          onClick={onClose}
          className="group inline-flex items-center gap-1 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/65 no-underline transition-colors duration-150 hover:text-[var(--ink)]"
        >
          <Trans>see all</Trans>
          <span aria-hidden className="mag-arrow mag-arrow-ne">
            ↗
          </span>
        </Link>
      </footer>
    </div>
  );
}

function DropdownStatus({ children }: { children: ReactNode }) {
  return (
    <p className="mag-voice px-3 py-6 text-center text-[14px] leading-snug text-[var(--ink)]/75">
      “{children}”
    </p>
  );
}

/** Loading placeholder — same brand-sparkle + pulse-dot language as
 *  the full-page PageLoader, scaled down for the dropdown. Keeps
 *  the visual vocabulary consistent: anywhere the app is fetching
 *  for the founder, the same spark animation marks the wait. */
function DropdownLoading() {
  return (
    <div className="flex flex-col items-center gap-3 px-3 py-8">
      <BrandSparkle size={32} color="var(--spell)" className="mag-spark-twinkle" />
      <div className="flex items-center gap-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-[var(--ink)]/55">
        <span className="mag-pulse inline-block h-1 w-1 rounded-full bg-[var(--pulse)]" />

        <Trans>loading</Trans>
      </div>
    </div>
  );
}

interface RowProps {
  notification: NotificationItem;
  /** The brand this notification belongs to (looked up from
   *  notification.brandId by the dropdown). Null for org-scope
   *  notifications (workspace-level) or when the brand row hasn't
   *  loaded yet — the row renders a neutral magig chip in either
   *  case so the founder always sees a sender. */
  brand: Brand | null;
  rowIndex: number;
  onMarkRead: () => void;
  onArchive: () => void;
  onOpen: () => void;
}

function NotificationRow({
  notification,
  brand,
  rowIndex,
  onMarkRead,
  onArchive,
  onOpen,
}: RowProps) {
  const { t } = useLingui();
  const unread = notification.readAt === null;
  const created = new Date(notification.createdAt);
  // Whole-row click → open the global notification drawer (the parent
  // wires onOpen to openNotification + dropdown-close). The drawer
  // shows the full details and carries the CTA navigation, so the row
  // no longer navigates directly.
  //
  // Inner action buttons (read / archive) are flex siblings of the
  // content button, not nested inside it — keeps the HTML valid
  // (no button-in-button) and means their clicks fire without
  // bubbling to the row handler.
  const handleRowClick = onOpen;
  // Hover bg shifts visibly in both read and unread variants:
  //   read   → white background, paper on hover (clear darker step)
  //   unread → paper background, spell-soft tint on hover (says
  //            "this is the unread one mag wants you to see")
  const rowBg = unread
    ? 'bg-[var(--paper)] hover:bg-[var(--spell-soft)]/35'
    : 'bg-white hover:bg-[var(--paper)]';
  return (
    <li
      style={{ ['--row-i' as string]: rowIndex }}
      className={`mag-dropdown-row-in group/row flex items-start gap-1 border-b border-[var(--line)] transition-colors last:border-b-0 ${rowBg}`}
    >
      <button
        type="button"
        onClick={handleRowClick}
        className="flex min-w-0 flex-1 items-start gap-2 px-3 py-2.5 text-left"
      >
        <span
          aria-hidden
          className={`mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full ${
            unread ? 'bg-[var(--pulse)]' : 'bg-transparent'
          }`}
        />
        <span className="min-w-0 flex-1">
          {/* Brand badge row — like Google Play's sender chip. Shows
              which brand the notification is for so the founder can
              triage across all brands from one panel without the
              kind tag carrying the weight alone. Org-scope
              notifications get a neutral "magig" chip so every row
              has a sender visible. */}
          <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <BrandChip brand={brand} fallbackName={notification.brandId ? '…' : 'magig'} />
            <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/55">
              {notification.kind} · {relativeTime(created)}
            </span>
          </span>
          <span className="mt-1 block font-display text-[13px] font-bold leading-tight tracking-[-0.01em] text-[var(--ink)]">
            {notification.title}
          </span>
          {/* line-clamp owns its own display (-webkit-box), so we
              don't pair it with `block` — the two compete and
              `block` wins in Tailwind v4's cascade, killing the
              clamp. Result without this: long notification bodies
              wrap to 5-6 lines and dominate the dropdown. */}
          <span className="mt-1 line-clamp-2 text-[12px] leading-snug text-[var(--ink)]/75">
            {notification.body}
          </span>
          {notification.cta ? (
            // Display-only — the whole row is the click target, so
            // this isn't a separate link. Arrow still animates on
            // row hover via the parent .group hover hook (the inner
            // <button> carries the .group class implicitly via the
            // li below).
            <span className="group mt-1.5 inline-flex items-center gap-1 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--plum)]">
              {notification.cta.label}
              <span aria-hidden className="mag-arrow mag-arrow-ne">
                ↗
              </span>
            </span>
          ) : null}
        </span>
      </button>
      <div className="flex shrink-0 flex-col items-end gap-1 py-2.5 pr-2 opacity-0 transition-opacity duration-150 group-hover/row:opacity-100">
        {unread ? (
          <button
            type="button"
            onClick={onMarkRead}
            title={t`mark read`}
            className="rounded px-1 font-mono text-[9px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/55 transition-colors duration-150 hover:bg-[var(--spell-soft)]/40 hover:text-[var(--ink)]"
          >
            <Trans>read</Trans>
          </button>
        ) : null}
        <button
          type="button"
          onClick={onArchive}
          title={t`archive`}
          className="rounded px-1 font-mono text-[9px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/55 transition-colors duration-150 hover:bg-[var(--pulse)]/10 hover:text-[var(--pulse)]"
        >
          <Trans>archive</Trans>
        </button>
      </div>
    </li>
  );
}

function PreferencesLink({ onClose }: { onClose: () => void }) {
  // Notifications prefs live inside per-brand settings (s178.7d).
  // Route to whichever brand is active; fall back to /brands when
  // none is selected so the founder picks one explicitly.
  const { data: brands = [] } = useQuery(brandsQuery());
  const activeBrandId = useActiveBrandId();
  const live = brands.filter((b) => b.archivedAt === null);
  const target = live.find((b) => b.id === activeBrandId) ?? live[0];
  const cls =
    'group inline-flex items-center gap-1 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/65 no-underline transition-colors duration-150 hover:text-[var(--ink)]';
  if (!target) {
    return (
      <Link to="/brands" onClick={onClose} className={cls}>
        <span aria-hidden className="mag-arrow mag-arrow-se">
          ↘
        </span>

        <Trans>preferences</Trans>
      </Link>
    );
  }
  return (
    <Link
      to="/brand/$slug/settings"
      params={{ slug: target.slug }}
      onClick={onClose}
      className={cls}
    >
      <span aria-hidden className="mag-arrow mag-arrow-se">
        ↘
      </span>

      <Trans>preferences</Trans>
    </Link>
  );
}

function relativeTime(d: Date): string {
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d`;
  return formatDate(d);
}

function BellGlyph({ ringing = false }: { ringing?: boolean }) {
  const { t } = useLingui();
  // Hand-drawn bell. Same line weight + corner rounding as the rest
  // of the SVG glyphs across the dashboard (mag, slack hash).
  //
  // `ringing` applies the mag-bell-ring keyframe — bell swings every
  // ~4.5s when there's unread, paused for the long majority of the
  // cycle so it doesn't read as nervous. Pauses when the dropdown is
  // open (passed as !open from the parent) so the bell isn't bouncing
  // while the founder is reading the notifications.
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={ringing ? 'mag-bell-ring' : undefined}
    >
      <title>{t`notifications`}</title>
      <path d="M3.5 11.5h9l-1.2-1.5V7a3.3 3.3 0 0 0-6.6 0v3l-1.2 1.5Z" />
      <path d="M6.5 13a1.5 1.5 0 0 0 3 0" />
    </svg>
  );
}
